<script setup lang="ts">
import { ConfirmationPopup, SwitchComponent } from '@metapic-apps/shared'
import type { FormInstance, FormRules } from 'element-plus'
import { computed, reactive, ref } from 'vue'

import { useAddEntriesToBlocklist } from '@/api/blocklist.api'
import TargetInput from '@/components/TargetInput/TargetInput.vue'
import { notification } from '@/lib/helpers'
import type { BlocklistFormType } from '@/lib/types/blocklist.types'
import { TargetItemEnum } from '@/lib/types/common.types'
import { BoxComponent, ButtonComponent, FlexComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{ advertiserId: number; advertiserGroupId: number }>()
const emits = defineEmits(['reload'])
const { addToBlocklist, isLoading, error } = useAddEntriesToBlocklist()
const isDirty = ref(false)
const isCreatorConfirmationOpen = ref(false)
const ruleFormRef = ref<FormInstance>()
const formData = reactive<BlocklistFormType>({
  targets: [],
  clients: [],
  hasUnmatchedTargets: false,
  blockFutureRevenue: false,
})

const rules = reactive<FormRules>({
  targets: [
    {
      validator: (_, value: string, callback) => {
        if (!value.length) {
          callback(new Error('Please select at least one creator, user tag or client'))
        } else if (formData.hasUnmatchedTargets) {
          callback(new Error('Delete unmatched creators'))
        } else {
          callback()
        }
      },
      trigger: 'submit',
    },
  ],
})

const creatorsList = computed(() => {
  return formData.targets.filter((target) => target.type === TargetItemEnum.User)
})

const confirmCreatorsBlock = async () => {
  if (!ruleFormRef.value) {
    return
  }
  isCreatorConfirmationOpen.value = false
  await submitForm(ruleFormRef.value)
}

const onSubmitButtonClick = (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }
  return formEl.validate(async (valid) => {
    if (valid) {
      isDirty.value = false

      if (creatorsList.value.length) {
        formData.blockFutureRevenue = false
        isCreatorConfirmationOpen.value = true
        return
      } else {
        await submitForm(formEl)
      }
    }
  })
}

const submitForm = async (formEl: FormInstance) => {
  await addToBlocklist(props.advertiserId, formData)
  if (!error.value) {
    emits('reload', true)
    notification.success({ message: 'Blocklist was updated successfully' })
    formEl.resetFields()
  }
}
</script>
<template>
  <div>
    <el-form
      ref="ruleFormRef"
      :model="formData"
      :rules="rules"
      hide-required-asterisk
      :validate-on-rule-change="false"
      :scroll-to-error="true"
      label-position="top"
    >
      <FlexComponent :gap="16">
        <TargetInput
          v-model:targets="formData.targets"
          v-model:has-unmatched-targets="formData.hasUnmatchedTargets"
          :visible-tabs="[TargetItemEnum.User, TargetItemEnum.UserTag, TargetItemEnum.Client]"
          :advertiser-group="advertiserGroupId"
          style="flex-grow: 1"
          name="targets"
        />
        <BoxComponent :mt="42">
          <ButtonComponent variant="primary" :is-loading="isLoading" @click="onSubmitButtonClick(ruleFormRef)"> Block </ButtonComponent>
        </BoxComponent>
      </FlexComponent>
    </el-form>
    <ConfirmationPopup
      v-model="isCreatorConfirmationOpen"
      :title="creatorsList.length === 1 ? 'Block creator' : 'Block creators'"
      submit-btn-text="Block"
      submit-btn-variant="primary"
      @on-submit="confirmCreatorsBlock"
    >
      <BoxComponent :mt="16" :mb="28">
        <TypographyComponent variant="body-large">
          Blocked creators won’t see the advertiser in the Creator App, can’t convert links, and can’t participate in campaigns, even if
          invited.
        </TypographyComponent>
      </BoxComponent>
      <BoxComponent :mb="28">
        <FlexComponent :gap="16" align="center">
          <SwitchComponent v-model="formData.blockFutureRevenue" name="blockFutureRevenue" />
          <TypographyComponent variant="body-large"> Stop links from generating revenue</TypographyComponent>
        </FlexComponent>
      </BoxComponent>
    </ConfirmationPopup>
  </div>
</template>
<style lang="scss" scoped></style>
